import React, { ChangeEvent, useMemo, useState } from 'react';
import { orderBy } from 'lodash';
import { Link, useNavigate } from 'react-router-dom';

import { getTutorials, setActiveTutorial } from '../../../store/tutorials';
import { tutorialConstants, USER_TYPES } from '../../../utils/constants';
import { getCurrentUser } from '../../../services/authService';

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

import CreateUser from '../../forms/createUser';
import Conditional from '../../common/conditional';
import Filters from '../../blocks/filters';
import Header from '../../blocks/header';
import Instruction from '../../themed/instruction-modals/instruction';
import Loader from '../../themed/loader';
import Modal from '../../themed/modal';
import NoItems from '../../blocks/no-items';
import Overview from '../../blocks/overview';
import Pagination from '../../themed/pagination';
import Select from '../../themed/select/select';
import SyncTime from '../../blocks/sync-time';
import Status from '../../blocks/status';
import Table from '../../common/table';

import withUsers from '../../hoc/withUsers';
import withRoles from '../../hoc/withRoles';

import classNames from './style.module.scss';
import routes from '../../../utils/routes';
import toastService from '../../../services/toastService';

import { resendWelcomeEmail } from '../../../services/userService';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';

interface UserRoleBreakdown {
	id: string;
	name: string;
	count: number;
}

interface UsersProps {
	users: models.User[];
	roles: models.Role[];
	rolesLoading: boolean;
	usersLastUpdated: number | null;
	usersLoading: boolean;
	refreshUsers: () => void;
}

const Users: React.FC<UsersProps> = ({
	roles,
	rolesLoading,
	refreshUsers,
	users,
	usersLastUpdated,
	usersLoading,
}) => {
	const [filterOption, setFilterOption] = useState<string>('');
	const [sortColumn, setSortColumn] = useState(initialSortColumn);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [pageSize, setPageSize] = useState<number>(12);
	const [filters, setFilters] = useState<models.FilterItem[]>([]);
	const [createUser, setCreateUser] = useState<boolean>(false);

	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const tutorials = useAppSelector(getTutorials);

	const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setFilterOption(e.target.value);
	};

	const user = getCurrentUser() as models.User;

	const roleUsers = useMemo((): models.User[] => {
		return filterOption
			? users.filter((u: models.User) => u.roleId === filterOption)
			: users;
	}, [filterOption, users]);

	const filteredUsers = useMemo(() => {
		return utils.getFilteredItemsFromFilters(filters, roleUsers);
	}, [filters, roleUsers]);

	const paginatedData = useMemo(() => {
		const filtered = filteredUsers;

		const sorted = orderBy(filtered, [sortColumn.path], [sortColumn.order]);
		const paginated = utils.paginate(sorted, currentPage, pageSize);

		return { totalCount: filtered.length, paginated };
	}, [currentPage, pageSize, sortColumn, filteredUsers]);

	const getRoleBreakdown = (): UserRoleBreakdown[] => {
		const rolesWithCount: UserRoleBreakdown[] = roles.map((role) => ({
			id: role.id,
			name: role.name,
			count: users.filter((user) => user.roleId === role.id).length,
		}));

		const firstThreeRoles = rolesWithCount.slice(0, 3);

		const otherRoles: UserRoleBreakdown = {
			id: 'others',
			name: 'Others',
			count: rolesWithCount.slice(3).reduce((acc, role) => acc + role.count, 0),
		};

		return [...firstThreeRoles, otherRoles];
	};

	const handleNewFilterChange = (newFilters: models.FilterItem[]) => {
		setFilters(newFilters);
		setCurrentPage(1);
	};

	const handlePageSizeChange = (e: ChangeEvent<HTMLSelectElement>) => {
		const pageSize: number = parseInt(e.target.value);
		const actualPageSize: number = pageSize === 0 ? 1 : pageSize;

		setPageSize(actualPageSize);
	};

	const handleViewNextTutorial = () => {
		if (tutorials.activeTutorial === tutorialConstants.MANAGE_USERS) {
			dispatch(setActiveTutorial(tutorialConstants.SETUP_DIVISIONS));
			navigateToAddDivision();
		} else {
			dispatch(setActiveTutorial(tutorialConstants.MANAGE_ORGANIZATIONS));
			navigate(routes.organizations.fullPath);
		}
	};

	const renderInstruction = () => {
		const activeTutorials = [
			tutorialConstants.MANAGE_USERS,
			tutorialConstants.SETUP_USER,
		];

		return (
			<Instruction
				isVisible={activeTutorials.includes(tutorials.activeTutorial as number)}
				onProceed={handleViewNextTutorial}
				title="Manage Users Here!"
				message="Here, you can setup user accounts, create divisions, assign privileges and much more!"
			/>
		);
	};

	const toggleCreateUser = (): void => {
		setCreateUser((p: boolean) => !p);
	};

	const navigateToAddRole = (): void => {
		navigate(`/dashboard/add-role`);
	};

	const navigateToAddDivision = (): void => {
		navigate(`/dashboard/divisions`);
	};


	return (
		<React.Fragment>
			{renderInstruction()}

			<Modal isVisible={createUser} style={{ height: 'auto' }} onClose={toggleCreateUser}>
				<CreateUser />
			</Modal>

			<Header subtitle="Showing Registered Users" />

			<div className={classNames.users}>
				<div className="container-fluid overflow-hidden mt-3">
					<div className="row gap-4">
						<div className="col p-0">
							<Overview
								iconName="bi-person-plus"
								title="Add New User"
								subtitle="Create New User Account"
								onClick={toggleCreateUser}
							/>
						</div>

						<Conditional isVisible={user.role.manageRoles}>
							<div className="col mx-3">
								<Overview
									iconName="bi-lock-fill"
									title="Roles & Permissions"
									subtitle="Modify user roles & permissions"
									onClick={navigateToAddRole}
								/>
							</div>
						
							<div className="col p-0">
								<Overview
									iconName="bi-collection-fill"
									title="Divisions"
									subtitle="Add or delete divisions"
									onClick={navigateToAddDivision}
								/>
							</div>
						</Conditional>

					</div>
				</div>

				<div className="divider my-4"></div>

				<div className={classNames.usersHeader}>
					<div className="d-flex justify-content-between">
						<div>
							<h4>{utils.formatDigits(users.length)} </h4>
							<p>Registered User(s)</p>
						</div>

						<div className="d-flex justify-content-center">
							<div className={classNames.usersHeaderOverview}>
								<i className="bi bi-lock-fill"></i>
								<span>{roles.length} Supported Roles</span>
							</div>
						</div>
					</div>
				</div>

				<div className="divider my-4"></div>

				<Loader isVisible={rolesLoading} />

				<Conditional isVisible={!rolesLoading}>
					<div className="container-fluid overflow-hidden my-4">
						<div className="row">
							{getRoleBreakdown().map((role) => (
								<div className="col ml-2" key={role.id}>
									<Overview
										iconName="bi-bookmark-star-fill"
										title={utils.summarize(role.name, 20)}
										subtitle={`${role.count} total users`}
									/>
								</div>
							))}
						</div>
					</div>
				</Conditional>

				<div className="divider my-4"></div>

				<SyncTime lastSynced={usersLastUpdated} />

				<div className="divider my-4"></div>

				<div className={classNames.usersBody}>
					<div className="d-flex justify-content-between align-items-center">
						<p className={classNames.usersBodyTitle}>All Users</p>

						<div className="d-flex justify-content-center bg-white p-2 pb-0 mb-3">
							<Select
								name="page-size"
								placeholder="All Users"
								options={roles}
								value={filterOption}
								onChange={handleFilterChange}
							/>

							<div className="ms-3">
								<Filters 
									filters={filters} 
									filterOptions={filterOptions} 
									onFilterChange={handleNewFilterChange} 
								/>
							</div>
						</div>
					</div>

					<div className={classNames.usersBodyItems}>
						<div className="bg-white p-3 border">
							<Table
								columns={columns}
								data={paginatedData.paginated}
								sortColumn={sortColumn}
								onSort={setSortColumn}
								isFetching={usersLoading}
							/>
						</div>

						<NoItems isVisible={users.length === 0} onRefresh={refreshUsers} />
					</div>

					<div className="d-flex justify-content-between align-items-center mt-3">
						<Select
							name="page-size"
							placeholder={'Showing (' + pageSize + 'rows)'}
							options={utils.transformPageRange(20)}
							value={pageSize}
							onChange={handlePageSizeChange}
						/>

						<Pagination
							itemsCount={paginatedData.totalCount}
							pageSize={pageSize}
							currentPage={currentPage}
							onPageChange={(page: number) => setCurrentPage(page)}
						/>
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

const currentUser = getCurrentUser() as models.User;

const filterOptions: models.SelectOption[] = [
	{ id: 'id', name: 'ID' },
	{ id: 'firstName', name: 'First Name' },
	{ id: 'lastName', name: 'Last Name' },
	{ id: 'designation', name: 'Designation' },
	{ id: 'divisionId', name: 'Division' },
	{ id: 'roleName', name: 'Role' },
	{ id: 'email', name: 'Email Address' },
	{ id: 'phone', name: 'Phone Number' },
	{ id: 'userType', name: 'User Type' },
];

const initialSortColumn: models.SortColumn = {
	path: 'firstName',
	order: 'asc',
};

const columns: models.Column[] = [
	{
		path: 'firstName',
		label: 'First Name',
	},
	{
		path: 'lastName',
		label: 'Last Name',
	},
	{
		path: 'email',
		label: 'Email',
	},
	{
		path: 'userType',
		label: 'User Type',
	},
	{
		path: 'roleName',
		label: 'Role',
	},
	{
		path: 'lastLogin',
		label: 'Last Login',
		content: (user) => (
			<span>{user.lastLogin ? utils.formatTime(user.lastLogin) : 'N/A'}</span>
		),
	},
	{
		path: 'isActive',
		label: 'Status',
		content: (user) => {
			const sendReactivationEmail = () => {
				toastService.promise(resendWelcomeEmail(user!.email), {
					success: `Email Resent Successfully!`,
					loading: 'Resending Activation Email',
					error: utils.getErrorMessage,
				});
			};

			return (
				<div className="d-flex gap-1 align-items-center justify-content-center">
					<div className="flex-fill">
						<Status isActive={user.isActive} />
					</div>

					<Conditional isVisible={currentUser.role.manageUsers && !user.isActive}>
						<button className={classNames.userActionButton} onClick={sendReactivationEmail}>
							<i className="bi bi-envelope"></i>
							Onboard
						</button>
					</Conditional>
				</div>
			);
		}
	},
	{
		path: '_id',
		label: '',
		content: (user: models.User) => {
			return (
				<Conditional isVisible={user.userType === USER_TYPES.EXTERNAL ? currentUser.role.manageRoles : currentUser.role.manageUsers}>
					<button className={classNames.viewMore}>
						<Link to={`/dashboard/users/${user.id}`}>View</Link>
					</button>
				</Conditional>
			)
		},
	},
];

export default withUsers(withRoles(Users));
